import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, CheckCircle, AlertCircle, ArrowLeft, Mic, Square } from 'lucide-react';
import server from '../../api/server';
import { useToast } from '../../context/ToastContext';
import ModernTimeDropdown from '../layouts/modernDropdown';
import { FaTimes } from 'react-icons/fa';

const PublicFormViewer = () => {
  const { showToast } = useToast();
  const { formId } = useParams();
  const navigate = useNavigate();
  
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [responses, setResponses] = useState({});
  const [fileUploads, setFileUploads] = useState({});
  const [audioRecordings, setAudioRecordings] = useState({});
  const [recordingQuestionId, setRecordingQuestionId] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  // Track whether the user has tried to submit the form at least once
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Load form data
  useEffect(() => {
    loadFormData();
  }, [formId]);

  useEffect(() => {
    // Only run validation automatically after the user has attempted a submit
    if (hasAttemptedSubmit && form) {
      validateForm();
    }
  }, [responses, form, hasAttemptedSubmit]);

  const hasUnsavedFormInput = useMemo(() => {
    const hasResponseData = Object.values(responses).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object') return true;
      if (value === null || value === undefined) return false;
      return value.toString().trim() !== '';
    });

    return (
      hasResponseData ||
      Object.keys(fileUploads).length > 0 ||
      Object.keys(audioRecordings).length > 0
    );
  }, [responses, fileUploads, audioRecordings]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (submitted || !hasUnsavedFormInput) return;
      event.preventDefault();
      // Required for Chrome to trigger the default unsaved-changes dialog.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [submitted, hasUnsavedFormInput]);

  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      Object.values(audioRecordings).forEach((recording) => {
        if (recording?.url) {
          URL.revokeObjectURL(recording.url);
        }
      });
    };
  }, [audioRecordings]);

  const loadFormData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await server.getPublicForm(formId);
      
      if (response.status === 200) {
        const data = await response.json();
        setForm(data.data);
        
        // Initialize responses object
        const initialResponses = {};
        data.data.questions.forEach(question => {
          initialResponses[question.id] = question.type === 'multi_select' ? [] : '';
        });
        setResponses(initialResponses);
      } else if (response.status === 404) {
        setError('Form not found or no longer available');
      } else {
        setError('Failed to load form');
      }
    } catch (error) {
      console.error('Error loading form:', error);
      setError('Error loading form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
    
    // Clear validation error for this question
    if (validationErrors[questionId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  // Validate form and return an object containing errors
  const validateForm = () => {
    const errors = {};

    if (!form) return errors;

    form.questions.forEach((question) => {
      if (!question.required) return;

      const response = responses[question.id];

      switch (question.type) {
        case 'multi_select':
          if (!response || response.length === 0) {
            errors[question.id] = 'This field is required';
          }
          break;
        default:
          if (
            response === null ||
            response === undefined ||
            response.toString().trim() === ''
          ) {
            errors[question.id] = 'This field is required';
          }
          break;
      }
    });

    setValidationErrors(errors);
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Mark that the user has attempted to submit at least once
    setHasAttemptedSubmit(true);
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      showToast('Please fix the highlighted errors before submitting.', 'error');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      const formData = new FormData();
      formData.append('form_id', formId);
      formData.append('responses', JSON.stringify(responses));

      // Append any files that were selected
      Object.entries(fileUploads).forEach(([qid, fileEntry]) => {
        if(Array.isArray(fileEntry)){
          fileEntry.forEach((f, idx) => {
            formData.append(`file_${qid}_${idx}`, f);
          });
        }else{
          formData.append(`file_${qid}`, fileEntry);
        }
      });
      formData.append('submitted_at', new Date().getTime());
      
      const response = await server.submitPublicForm(formData);
      
      if (response.status === 200) {
        setSubmitted(true);
      } else {
        setError('Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('Error submitting form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = (questionId, files, allowMultiple = false) => {
    if (!files || files.length === 0) return;
    
    // Check file count limit for multiple uploads
    if (allowMultiple && files.length > 20) {
      showToast('Maximum 20 files allowed', 'error');
      setValidationErrors(prev => ({ ...prev, [questionId]: 'Maximum 20 files allowed' }));
      return;
    }
    
    const acceptedFiles = allowMultiple ? Array.from(files) : [files[0]];
    const validFiles = [];
    for (const file of acceptedFiles) {
      if (file.size > 25*1024*1024) {
        // store the oversized file so user can remove it
        setFileUploads(prev => ({ ...prev, [questionId]: allowMultiple ? acceptedFiles : acceptedFiles[0] }));
        showToast('File size exceeds 25MB', 'error');
        setValidationErrors(prev => ({ ...prev, [questionId]: 'File size exceeds 25 MB' }));
        return;
      }
      if (!(file.name.toLowerCase().endsWith('.m4a') || file.name.toLowerCase().endsWith('.wav') || file.name.toLowerCase().endsWith('.mp3') || file.name.toLowerCase().endsWith('.aac') || 
      file.name.toLowerCase().endsWith('.ogg') || file.name.toLowerCase().endsWith('.wma') || file.name.toLowerCase().endsWith('.mp4a') || file.name.toLowerCase().endsWith('.mp4'))){
        // store the unsupported file so user can remove it
        setFileUploads(prev => ({ ...prev, [questionId]: allowMultiple ? acceptedFiles : acceptedFiles[0] }));
        showToast('Unsupported file format', 'error');
        setValidationErrors(prev => ({ ...prev, [questionId]: 'Unsupported file format' }));
        return;
      }
      validFiles.push(file);
    }

    // store files (array or single)
    setFileUploads(prev => ({ ...prev, [questionId]: allowMultiple ? validFiles : validFiles[0] }));

    const metas = validFiles.map(f => ({ type: 'audio', file_name: f.name }));
    handleResponse(questionId, allowMultiple ? metas : metas[0]);

    setValidationErrors(prev => {
      if (!prev[questionId]) return prev;
      const copy = { ...prev };
      delete copy[questionId];
      return copy;
    });
  };

  const removeFile = (questionId, index = null, allowedTypes = []) => {
    // clone current uploads
    const curr = fileUploads[questionId];
    if (!curr) return;

    let updatedUploads;
    if (Array.isArray(curr)) {
      const arr = [...curr];
      if (index !== null) {
        arr.splice(index, 1);
      }
      updatedUploads = arr.length ? arr : undefined;
    } else {
      updatedUploads = undefined; // single file removed
    }

    // Build new fileUploads object
    const newFileUploads = { ...fileUploads };
    if (updatedUploads) {
      newFileUploads[questionId] = updatedUploads;
    } else {
      delete newFileUploads[questionId];
    }
    setFileUploads(newFileUploads);

    // Re-validate remaining files
    if (!updatedUploads) {
      // nothing left – clear response & error
      handleResponse(questionId, '');
      setValidationErrors(prev => {
        const c = { ...prev };
        delete c[questionId];
        return c;
      });
      return;
    }

    const arr = Array.isArray(updatedUploads) ? updatedUploads : [updatedUploads];
    const isAllowedFile = (fileObj) => {
      if (!allowedTypes || allowedTypes.length === 0) return true;
      return allowedTypes.some((pattern) => {
        if (pattern.endsWith('/*')) {
          const prefix = pattern.split('/')[0];
          return fileObj.type.startsWith(prefix + '/');
        }
        if (pattern.startsWith('.')) {
          return pattern.split(',').some(ext => fileObj.name.toLowerCase().endsWith(ext.trim().toLowerCase()));
        }
        return fileObj.type === pattern;
      });
    };

    let invalid = false;
    const metas = [];
    for (const file of arr) {
      if (file.size > 25 * 1024 * 1024 || !isAllowedFile(file)) {
        invalid = true;
        break;
      }
      metas.push({ type: 'file', file_name: file.name });
    }

    if (invalid) {
      handleResponse(questionId, '');
      setValidationErrors(prev => ({ ...prev, [questionId]: 'Invalid file present' }));
    } else {
      handleResponse(questionId, Array.isArray(updatedUploads) ? metas : metas[0]);
      setValidationErrors(prev => {
        const c = { ...prev };
        delete c[questionId];
        return c;
      });
    }
  };

  const formatDuration = (durationSec) => {
    const minutes = Math.floor(durationSec / 60);
    const seconds = durationSec % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const startRecording = async (questionId) => {
    if (recordingQuestionId) {
      showToast('Finish the current recording first', 'error');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const ext = mediaRecorder.mimeType?.includes('ogg') ? 'ogg' : 'webm';
        const mimeType = mediaRecorder.mimeType || `audio/${ext}`;
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const filename = `recording-${questionId}-${Date.now()}.${ext}`;
        const file = new File([blob], filename, { type: mimeType });
        const url = URL.createObjectURL(blob);

        setAudioRecordings((prev) => {
          if (prev[questionId]?.url) {
            URL.revokeObjectURL(prev[questionId].url);
          }
          return {
            ...prev,
            [questionId]: {
              url,
              file,
              duration: recordingDuration
            }
          };
        });

        setFileUploads((prev) => ({ ...prev, [questionId]: file }));
        handleResponse(questionId, {
          type: 'audio',
          file_name: filename
        });

        stream.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        setRecordingQuestionId(null);
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
          recordingIntervalRef.current = null;
        }
      };

      setRecordingDuration(0);
      setRecordingQuestionId(questionId);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
      mediaRecorder.start();
    } catch (err) {
      console.error('Microphone access error:', err);
      showToast('Microphone access denied or not available', 'error');
    }
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      return;
    }
    mediaRecorderRef.current.stop();
  };

  const renderQuestion = (question) => {
    const hasError = validationErrors[question.id];
    
    return (
      <div key={question.id} className={`border ${hasError ? 'border-red-500' : ''} p-4 rounded-md shadow-sm bg-white`}>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {question.title || 'Untitled Question'}
            {question.required && <span className="text-red-500 ml-1">*</span>}
          </h3>
          {question.description && (
            <p className="text-gray-600 mt-1">{question.description}</p>
          )}
          {hasError && (
            <p className="text-red-500 text-sm mt-1">{validationErrors[question.id]}</p>
          )}
        </div>

        <div className="mb-6">
          {question.type === 'text' && (
            <input
              type="text"
              placeholder={question.placeholder}
              value={responses[question.id] || ''}
              onChange={(e) => handleResponse(question.id, e.target.value)}
              className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:border-transparent ${
                hasError 
                  ? 'border-red-300 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-indigo-500'
              }`}
            />
          )}
          {question.type === 'textarea' && (
            <textarea
              placeholder={question.placeholder}
              value={responses[question.id] || ''}
              onChange={(e) => handleResponse(question.id, e.target.value)}
              className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:border-transparent resize-none ${
                hasError 
                  ? 'border-red-300 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-indigo-500'
              }`}
              rows="3"
            />
          )}

          {question.type === 'single_select' && (
            <div className="space-y-3">
              {question.options.map((option, idx) => (
                <label key={idx} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name={`question_${question.id}`}
                    value={option}
                    checked={responses[question.id] === option}
                    onChange={(e) => handleResponse(question.id, e.target.value)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 accent-indigo-600 border-gray-300"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.type === 'multi_select' && (
            <div className="space-y-3">
              {question.options.map((option, idx) => (
                <label key={idx} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    value={option}
                    checked={(responses[question.id] || []).includes(option)}
                    onChange={(e) => {
                      const currentValues = responses[question.id] || [];
                      const newValues = e.target.checked
                        ? [...currentValues, option]
                        : currentValues.filter(v => v !== option);
                      handleResponse(question.id, newValues);
                    }}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 accent-indigo-600 border-gray-300"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.type === 'dropdown' && (
            <ModernTimeDropdown width='w-full' placeholder='Select an option...' options={question.options} value={responses[question.id] || ''} onChange={(e) => handleResponse(question.id, e.target.value)} />
          )}

          {question.type === 'upload' && (
            <div className={`border-2 border-dashed rounded-lg p-8 text-center hover:border-indigo-400 transition-colors ${
              hasError ? 'border-red-300' : 'border-gray-300'
            }`}>
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
              <p className="text-xs text-gray-500 mb-4">
                Supported formats: {question.fileTypes.join(', ')}
                {question.allowMultiple && <span className="block mt-1">Maximum 20 files allowed</span>}
              </p>
              <input
                type="file"
                accept={question.fileTypes.join(',')}
                multiple={question.allowMultiple}
                onChange={(e) => handleFileUpload(question.id, e.target.files, question.allowMultiple)}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              />
              {fileUploads[question.id] && !Array.isArray(fileUploads[question.id]) && (
                <div className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                  <span>File selected: {fileUploads[question.id].name}</span>
                  <FaTimes onClick={() => removeFile(question.id, null, question.fileTypes)} className="cursor-pointer h-4 w-4" />
                </div>
              )}
              {Array.isArray(fileUploads[question.id]) && fileUploads[question.id].length > 0 && (
                <div className="mt-2 text-sm text-gray-600 flex flex-col gap-1">
                  {fileUploads[question.id].map((f, idx)=> (
                    <span key={idx} className="flex items-center gap-2">
                      {f.name}
                      <FaTimes onClick={() => removeFile(question.id, idx, question.fileTypes)} className="cursor-pointer h-4 w-4" />
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {question.type === 'audio_recording' && (
            <div className={`border-2 border-dashed rounded-lg p-6 ${hasError ? 'border-red-300' : 'border-gray-300'}`}>
              {recordingQuestionId === question.id ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="text-sm font-medium text-red-600">
                    Recording: {formatDuration(recordingDuration)}
                  </div>
                  <button
                    type="button"
                    onClick={stopRecording}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Square size={16} />
                    Stop Recording
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-start">
                  {!audioRecordings[question.id] && <button
                    type="button"
                    onClick={() => startRecording(question.id)}
                    className=" flex items-center gap-2 text-sm text-gray-500 py-2 px-4 rounded-full border-0 text-sm font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                  >
                    <Mic size={16} />
                    Start Recording
                  </button>}
                  {audioRecordings[question.id] && (
                    <>
                      <audio controls src={audioRecordings[question.id].url} className="w-full max-w-md" />
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Form Not Available</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <ArrowLeft size={18} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Form Submitted Successfully!</h2>
          <p className="text-gray-600 mb-6">Thank you for your submission. Your response has been recorded.</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <ArrowLeft size={18} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Form not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-2xl mx-auto px-4">
        <div>
          <div className="mb-4 p-4 bg-white rounded-md shadow-sm border-t-4 border-indigo-500">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {form.title}
            </h1>
            {form.description && (
              <p className="text-gray-600 text-lg leading-relaxed">
                {form.description}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {form.questions.map(renderQuestion)}
            </div>

            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            )}

            <div className="pt-6 flex justify-end">
              {(() => {
                const hasErrors = Object.keys(validationErrors).length > 0;
                return (
                  <button
                    type="submit"
                    disabled={submitting || hasErrors}
                    className={`flex items-center gap-2 ${submitting || hasErrors ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50`}
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>Submit Form</>
                    )}
                  </button>
                );
              })()}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicFormViewer; 